import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { Observable, Subscription, filter, map, of } from 'rxjs';
import { MessageService } from 'primeng/api';
import { slice } from 'lodash';

import { ChatMessageTypeModel } from '@api/ai/enums/AI-chat-completion.enum';
import { BriefingsApi } from '@api/briefings/briefings.api';

import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { AbstractPageBuilderService } from '@Common/PageBuilder/services/abstract-page-builder.service';
import { MentionInsightTypes } from '@Common/Mentions/enums/mention-insight-types.enum';
import { promptLanguageOptions } from '@Common/Forms/data/prompt-language-options';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { mentionInsightTypesOptions } from '@Common/Forms/data/mention-insight-types-options';

import { AbstractDialogComponent } from '@Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';

import { CuratedMentionListDisplayElementModel } from '../../models/curated-mention-list-display-element.model';
import { BriefingAutoGenerateInsight } from '../../models/briefing-auto-gen-insight.model';
import { BriefingsService } from '../../services/briefings.service';
import { CuratedAutoWebsocketDialogComponent } from '../CuratedAutoWebsocketDialog/curated-auto-websocket-dialog.component';
import { AutoGenerateInsightResponse, BriefingsAutoGenService } from '../../services/briefings-auto-gen.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { CustomInsights } from '@Monitor/Shared/models/custom-insights.model';
import { CoverageAnalysisService } from '@Monitor/Shared/services/coverage-analysis.service';
import { CustomInsightType } from '@Monitor/Shared/models/custom-insights-type.model';
import { TextInputDialogModel } from '@Common/Dialogs/models/text-input-dialog.model';
import { TextInputDialogComponent } from '@Common/Dialogs/components/TextInputDialog/text-input-dialog.component';

@Component({
    selector: 'agpr-curated-auto-insight-dialog',
    templateUrl: './curated-auto-insight-dialog.component.html',
    styleUrls: ['./curated-auto-insight-dialog.component.scss'],
    standalone: false
})
export class CuratedAutoInsightDialogComponent extends AbstractDialogComponent implements OnDestroy, OnInit {

    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly mentionInsightTypes = MentionInsightTypes;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() briefingId: number;

    id: string = 'curated_auto_insight_dialog';
    blockTitle: string;
    hideSlideOverlay: boolean = false;
    isMentionKeywords: boolean = true;
    saveAllMentions: boolean = false;
    withoutInsightsOnly: boolean = true;
    setPreferred: boolean = true;
    totalMentions: number = 0;
    totalMentionsWithoutInsights: number = 0;
    numMentionsInsideBlock: number = 0;
    numMentionsInsideBlockWithoutInsights: number = 0;
    numMentions: number = 0;
    mentionKeywordContext: string[] = [];
    manualKeywords: string[] = [];
    allCuratedMentions: string[] = [];
    allCuratedMentionsWithoutInsights: string[] = [];
    currentBlockMentions: string[] = [];
    currentBlockMentionsWithoutInsights: string[] = [];
    blockReferenceId: string;

    mentionInsightTypesOptions = mentionInsightTypesOptions;
    selectedMentionInsightType: DropdownOptionModel;

    promptLanguageOptions = promptLanguageOptions;
    selectedPromptLanguageOption: DropdownOptionModel;

    selectedCustomInsight: CustomInsights;
    customInsightCopy: CustomInsights;
    allowCustomInsightSave: boolean = false;
    saveCustomInsightTooltip: string = 'Mention.workbench.insights.prompt.custom.prompt.save.tooltip';
    saveAsCustomInsightTooltip: string = 'Mention.workbench.insights.prompt.custom.prompt.saveAs.tooltip';
    customInsightsOptions: DropdownOptionModel[];
    selectedCustomInsightOption: DropdownOptionModel;
    generateButtonTooltip: string = '';

    private subscriptions: Subscription[] = [];

    constructor(
        private briefingApi: BriefingsApi,
        public coreDialogService: CoreDialogService,
        private messageService: MessageService,
        private briefingsService: AbstractPageBuilderService,
        private briefingsAutoGenService: BriefingsAutoGenService,
        private currentUserStateService: CurrentUserStateService,
        private coverageAnalysisService: CoverageAnalysisService
    ) {
        super(coreDialogService, 'CuratedAutoInsightDialog');
        this.selectedPromptLanguageOption = this.promptLanguageOptions.find(option => option.value === 'en-US');
        this.selectedMentionInsightType = this.mentionInsightTypesOptions.find(option => option.value === MentionInsightTypes.CONTEXTUAL_ANALYSIS);
    }

    ngOnInit(): void {
        this.subscriptions.push(
            (this.briefingsService as BriefingsService).getCurrentBriefingMentions().subscribe(
                mentions => {
                    this.totalMentions = mentions?.length;
                    this.allCuratedMentions = [];
                    mentions.forEach(mention => {
                        if (mention.canGenerateInsight) {
                            let id;
                            if (mention.mentionId) {
                                id = mention.mentionId;
                            } else if (mention.id) {
                                id = mention.id;
                            }
                            if (id && !this.allCuratedMentions.includes(id)) {
                                this.allCuratedMentions.push(id);

                                if (!mention.insights?.length) {
                                    this.allCuratedMentionsWithoutInsights.push(id);
                                }
                            }
                        }
                    });

                    const mentionsWithoutInsights = mentions.filter(mention => !mention.insights?.length);
                    this.totalMentionsWithoutInsights = mentionsWithoutInsights?.length;
                    this.allCuratedMentionsWithoutInsights = mentionsWithoutInsights.map(mention => mention.mentionId || mention.id);
                    this.handleNumberOfMentions();
                }
            ),

            this.briefingsService.getSelectedBlock().pipe(filter(x => !!x)).subscribe(
                (block) => {
                    this.blockTitle = (block.displayElement as CuratedMentionListDisplayElementModel)?.title;
                    this.blockReferenceId = (block.displayElement as CuratedMentionListDisplayElementModel)?.blockReferenceId;
                    this.currentBlockMentions = [];
                    this.currentBlockMentionsWithoutInsights = [];
                    (block.displayElement as CuratedMentionListDisplayElementModel)?.mentions.forEach(mention => {
                        if (mention.canGenerateInsight) {
                            let id;
                            if (mention.id) {
                                id = mention.id;
                            } else if (mention.mercuryId) {
                                id = mention.mercuryId;
                            }
                            if (id && !this.currentBlockMentions.includes(id)) {
                                this.currentBlockMentions.push(id);

                                if (!mention.insights?.length) {
                                    this.currentBlockMentionsWithoutInsights.push(id);
                                }
                            }
                        }
                    });
                    this.numMentionsInsideBlock = this.currentBlockMentions?.length;
                    this.numMentionsInsideBlockWithoutInsights = this.currentBlockMentionsWithoutInsights?.length;
                    this.handleNumberOfMentions();
                }
            ),
            this.currentUserStateService.getUILanguageTag().subscribe(tag => {
                this.promptLanguageOptions.forEach(option => {
                    option.label = tag + '.' + option.value;
                });
            }),
            this.coverageAnalysisService.customInsights$.pipe(
                map(x => x.filter(item => item.promptType === CustomInsightType.MENTION_INSIGHTS))
            ).subscribe(customInsights => {
                this.customInsightsOptions = [];
                const newCustomPrompt = new CustomInsights();
                newCustomPrompt.promptType = CustomInsightType.MENTION_INSIGHTS;
                this.customInsightsOptions.push(new DropdownOptionModel('Briefings.analysis.customInsight.new', newCustomPrompt));
                if (customInsights?.length) {
                    for (const customInsight of customInsights) {
                        this.customInsightsOptions.push(new DropdownOptionModel(customInsight.name, customInsight.clone()));
                    }
                }
            })
        );
    }

    handleNumberOfMentions() {
        if (this.withoutInsightsOnly) {
            this.numMentions = this.saveAllMentions ? this.totalMentionsWithoutInsights : this.numMentionsInsideBlockWithoutInsights;
        } else {
            this.numMentions = this.saveAllMentions ? this.totalMentions : this.numMentionsInsideBlock;
        }

        this.updateGenerateButtonTooltip();
    }

    updateGenerateButtonTooltip(): void {
        if (this.numMentions === 0) {
            this.generateButtonTooltip =  'Briefings.blockType.autoGeninsights.noInsightsToGenerate';
            return;
        }

        if (this.selectedMentionInsightType?.value === MentionInsightTypes.CUSTOM
            && (this.allowCustomInsightSave || !this.selectedCustomInsightOption?.value?.id)
        ) {
            this.generateButtonTooltip =  'Briefings.blockType.autoGeninsights.saveCustomInsight';
            return;
        }

        this.generateButtonTooltip = '';
    }

    close(): void {
        this.ngOnDestroy();
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    apply() {
        const autoGenPayload: BriefingAutoGenerateInsight = {
            completionFormat: `${ChatMessageTypeModel.MENTION_INSIGHTS}_${this.selectedMentionInsightType.value}`,
        };

        if (this.withoutInsightsOnly) {
            autoGenPayload.mentionWithoutInsightsOnly = this.withoutInsightsOnly;
        } else {
            autoGenPayload.mentionWithoutInsightsOnly = false;
        }
        if (this.setPreferred) {
            autoGenPayload.preferred = this.setPreferred;
        } else {
            autoGenPayload.preferred = false;
        }
        if (this.selectedMentionInsightType.value !== MentionInsightTypes.QUOTES) {
            autoGenPayload.language = this.selectedPromptLanguageOption.value;
        }
        if (!this.isMentionKeywords && this.manualKeywords?.length) {
            autoGenPayload.customKeywords = this.manualKeywords;
        }

        if (this.saveAllMentions) {
            autoGenPayload.mentionIds = this.withoutInsightsOnly ? this.allCuratedMentionsWithoutInsights : this.allCuratedMentions;
        } else {
            autoGenPayload.mentionIds = this.withoutInsightsOnly ? this.currentBlockMentionsWithoutInsights : this.currentBlockMentions;
        }

        // we only want to send the first 100
        autoGenPayload.mentionIds = slice(autoGenPayload.mentionIds, 0, 100);

        if (!this.saveAllMentions) {
            autoGenPayload.blockReferenceId = this.blockReferenceId;
        }

        if (this.selectedMentionInsightType.value === MentionInsightTypes.CUSTOM) {
            autoGenPayload.customPromptId = this.selectedCustomInsightOption.value?.id;
        }

        this.briefingApi.generateCurratedAutoGen(this.briefingId, autoGenPayload).subscribe({
            next: () => {
                this.close();

                const autoGensStatus: AutoGenerateInsightResponse = {
                    resourceId: {
                        briefing: String(this.briefingId)
                    },
                    stats: {
                        insightsCompleted: 0,
                        totalInsightsToComplete: Math.min(this.numMentions, 100),
                        notEnoughContentMentions: 0,
                        failedToGenerateInsightMentions: 0,
                    },
                    count: 0,
                    showDialog: true,
                    loading: true
                };

                this.briefingsAutoGenService.setSpecificAutoGenStatus(this.briefingId, autoGensStatus);

                const dialogIO = {
                    inputs: {
                        briefingId: this.briefingId
                    },
                    outputs: {}
                };
                this.coreDialogService.open(CuratedAutoWebsocketDialogComponent, dialogIO);
            },
            error: (error) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Briefings.blockType.autoGeninsights.label',
                    detail: 'Briefings.blockType.autoGeninsights.generate.errorMessage'
                });
            }
        });
    }

    saveCustomInsight(): void {
        if (this.selectedCustomInsightOption.value?.id) {
            this.coverageAnalysisService.updateCustomInsight(this.selectedCustomInsightOption.value).subscribe(success => {
                if (success) {
                    this.customInsightCopy = this.selectedCustomInsightOption.value.clone();
                    this.allowCustomInsightSave = false;
                }
            });
        } else {
            this.doCreateCustomInsight();
        }
    }

    saveAsCustomInsight(): void {
        const io: TextInputDialogModel = {
            inputs: {
                id: 'nameCustomInsight',
                heading: 'Briefings.analysis.customInsight.header',
                okLabel: 'Briefings.analysis.customAnalysis.save',
                placeholder: 'Briefings.analysis.customAnalysisName.placeholder',
                description: 'Briefings.analysis.customInsight.label',
                initialValue: this.selectedCustomInsightOption.value.name,
                submit: (name: string) => {
                    this.selectedCustomInsightOption.value.name = name;
                    this.doCreateCustomInsight();
                    return of(null);
                }
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    doCreateCustomInsight() {
        this.coverageAnalysisService.createCustomInsight(this.selectedCustomInsightOption.value.clone()).subscribe(customInsightId => {
            if (customInsightId) {
                this.allowCustomInsightSave = false;
            }
        });
    }

    selectCustomInsightOption(): void {
        this.customInsightCopy = this.selectedCustomInsightOption.value.clone();
        this.customInsightFieldChanged();
    }

    customInsightFieldChanged(): void {
        if (this.selectedCustomInsightOption.value?.id) {
            this.allowCustomInsightSave = !this.selectedCustomInsightOption.value.equals(this.customInsightCopy);
        } else {
            this.allowCustomInsightSave = true;
        }
        this.updateGenerateButtonTooltip();

        if (!this.selectedCustomInsightOption.value.name?.trim()) {
            this.allowCustomInsightSave = false;
            this.saveCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.name.required';
            this.saveAsCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.name.required';
            this.updateGenerateButtonTooltip();

            return;
        }

        if (!this.selectedCustomInsightOption.value.prompt?.trim()) {
            this.allowCustomInsightSave = false;
            this.saveCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.required';
            this.saveAsCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.required';
            this.updateGenerateButtonTooltip();

            return;
        }

        this.saveCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.save.tooltip';
        this.saveAsCustomInsightTooltip = 'Mention.workbench.insights.prompt.custom.prompt.saveAs.tooltip';
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
